/**
 * CroTile — big-number-vs-target tile for the 9 CRO scalar metrics.
 *
 * Renders (per UI-SPEC §"Tile anatomy — big-number-vs-target"):
 *   - UX-07 4-state machine (carry-forward from Metric01Tile.tsx):
 *       loading → 3× Skeleton placeholders
 *       CacheNotReadyError → amber "Cache warming up" banner
 *       SalesforceUnavailableError → <SfUnreachableBanner>
 *       success → big-number tile
 *   - RESEARCH.md §8.5 confidence guard: confidence==='low' adds amber
 *     "⚠ Field names pending audit" badge above the value
 *   - % to target chip (omitted when target_value === null; CRO-05 + CRO-11)
 *     green >= 100%, amber >= 80%, red < 80%
 *   - Tooltip with definition + formula + source_soql from definitions.json
 *   - "View source records →" placeholder (drilldown wired in Phase 6)
 *
 * Used for: CRO-01, CRO-02, CRO-03, CRO-04, CRO-05, CRO-08, CRO-09, CRO-10, CRO-11
 */
import {
  CacheNotReadyError,
  SalesforceUnavailableError,
} from '../lib/api-client.js';
import { getCroMetricDefinition } from '../lib/definitions.js';
import { formatFiscalPeriod, formatPercentToTarget } from '../lib/fiscal.js';
import { formatCurrency, formatCount } from '../lib/format.js';

import { SfUnreachableBanner } from './SfUnreachableBanner.js';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card.js';
import { Skeleton } from './ui/Skeleton.js';
import { Tooltip } from './ui/Tooltip.js';

import type { CroMetricResponse } from '@saledash/shared';

interface CroTileProps {
  metricId: string;        // e.g. 'CRO-01'
  data: CroMetricResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

/**
 * Format the KPI value based on the metric's value_unit from definitions.json.
 * Currency metrics → formatCurrency (compact $M/$K)
 * Count/ratio metrics → formatCount (plain number)
 */
function formatValue(value: number, metricId: string): string {
  // Currency metrics: CRO-01, CRO-02, CRO-03, CRO-04, CRO-05, CRO-08, CRO-09, CRO-10, CRO-11
  // CRO-06 and CRO-07 are trend tiles handled by CroTrendTile, not this component.
  const currencyMetrics = new Set([
    'CRO-01', 'CRO-02', 'CRO-03', 'CRO-04', 'CRO-05',
    'CRO-08', 'CRO-09', 'CRO-10', 'CRO-11',
  ]);
  if (currencyMetrics.has(metricId)) {
    return formatCurrency(value);
  }
  return formatCount(value, 1);
}

/**
 * WoW / MoM delta pill (#1, 2026-07-09). The 9 scalar CRO tiles are all currency
 * and higher-is-better, so the delta formats as compact currency and up = green,
 * down = red. `prior === null` → honest "building" state (snapshot history not
 * deep enough yet — WoW needs ~7d, MoM ~30d), never a fabricated delta.
 */
function DeltaPill({
  label,
  current,
  prior,
}: {
  label: string;
  current: number;
  prior: number | null;
}): React.JSX.Element {
  if (prior === null) {
    return (
      <span
        className="inline-flex items-center rounded-full bg-ink-100 px-2 py-0.5 text-[11px] font-medium text-ink-500"
        data-testid={`delta-${label.toLowerCase()}`}
      >
        {label} · building
      </span>
    );
  }
  const diff = current - prior;
  const glyph = diff > 0 ? '▲' : diff < 0 ? '▼' : '—';
  const cls =
    diff > 0
      ? 'bg-good-bg text-good-text'
      : diff < 0
        ? 'bg-bad-bg text-bad-text'
        : 'bg-ink-100 text-ink-500';
  const amount = diff === 0 ? 'flat' : formatCurrency(Math.abs(diff));
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums ${cls}`}
      data-testid={`delta-${label.toLowerCase()}`}
      aria-label={`${label} ${amount}`}
    >
      <span aria-hidden>{glyph}</span> {label} {amount}
    </span>
  );
}

export function CroTile({
  metricId,
  data,
  isLoading,
  isError,
  error,
  refetch,
}: CroTileProps): React.JSX.Element {
  // Lazy-load definition (throws if metricId not found — surfaces at dev time).
  const definition = getCroMetricDefinition(metricId);

  const tooltipContent = (
    <>
      <p className="mb-1 font-semibold text-ink-900">{definition.name}</p>
      <p>{definition.formula_plain_english}</p>
      <p className="mt-2 text-[10px] uppercase tracking-wide text-ink-500">
        Source: {definition.source_of_truth_system}
      </p>
    </>
  );

  // UX-07: cache-warming → polite amber banner (BEFORE the SF-unreachable branch,
  // since CacheNotReadyError is a more specific 503 vs the generic 502).
  if (isError && error instanceof CacheNotReadyError) {
    return (
      <Card data-testid={`cro-tile-${metricId}`}>
        <CardHeader>
          <CardTitle>{definition.name}</CardTitle>
          <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
        </CardHeader>
        <CardContent>
          <div
            role="status"
            className="flex items-start gap-3 rounded-lg border border-warn bg-warn-bg px-4 py-3 text-sm text-warn-text"
            data-testid="cache-warming-banner"
          >
            <span
              aria-hidden
              className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-warn text-xs font-bold text-white"
            >
              ⌛
            </span>
            <div className="flex-1">
              <p className="font-semibold">Cache warming up</p>
              <p className="mt-1 text-xs text-warn-text/80">
                First data refresh expected within ~7 minutes. The sync worker
                will populate this tile on the next poll.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // UX-07: SF-unreachable → inline banner (page chrome stays mounted)
  if (isError && error instanceof SalesforceUnavailableError) {
    return (
      <Card data-testid={`cro-tile-${metricId}`}>
        <CardHeader>
          <CardTitle>{definition.name}</CardTitle>
          <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
        </CardHeader>
        <CardContent>
          <SfUnreachableBanner onRetry={refetch} />
        </CardContent>
      </Card>
    );
  }

  // UX-07: loading → 3 skeletons
  if (isLoading || !data) {
    return (
      <Card data-testid={`cro-tile-${metricId}`}>
        <CardHeader>
          <CardTitle>{definition.name}</CardTitle>
          <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-3 w-40" />
            <Skeleton className="h-5 w-20" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Plan 11 item #11 (RESEARCH §E.3 Option X) — placeholder branch.
  //
  // When the API response carries `placeholder_text` (CRO-11 paused-until-Q3
  // 2026), render a text-only PlaceholderCard instead of the big-number /
  // chip / sparkline layout. Data-driven: any future tile that needs a pause
  // state opts in by writing placeholder_text to its agg_cro_scorecard row.
  //
  // CRO-11 (Qtr Forecast Variance) override: stakeholder feedback 2026-06-11.
  // Forecast variance is only meaningful after the CRO's monthly forecast call,
  // which happens on the 15th. Override the placeholder text with a date-aware
  // "Not available until the 15th of [Month]" message until either (a) today
  // is on/after the 15th AND (b) the API returns real data (placeholder_text
  // is null). Compute the next 15th: current month if day < 15, else next month.
  if (data.placeholder_text || metricId === 'CRO-11') {
    let placeholderMessage = data.placeholder_text ?? '';
    if (metricId === 'CRO-11') {
      const today = new Date();
      const day = today.getDate();
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- HTTP JSON field may be absent (undefined); guard kept
      const noData = data.placeholder_text !== null && data.placeholder_text !== undefined;
      if (day < 15 || noData) {
        const nextFifteenth =
          day < 15
            ? today
            : new Date(today.getFullYear(), today.getMonth() + 1, 1);
        const monthName = nextFifteenth.toLocaleString('en-US', { month: 'long' });
        placeholderMessage = `Not available until the 15th of ${monthName}.`;
      }
    }
    if (placeholderMessage) {
      return (
        <Card data-testid={`cro-tile-${metricId}`}>
          <CardHeader>
            <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
              {definition.name}
            </CardTitle>
            <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
          </CardHeader>
          <CardContent>
            <p
              className="text-sm text-ink-700"
              data-testid="placeholder-text"
            >
              {placeholderMessage}
            </p>
          </CardContent>
        </Card>
      );
    }
  }

  // Success path — render big-number tile per UI-SPEC §"Tile anatomy — big-number-vs-target"
  const formattedValue = formatValue(data.value, metricId);

  // Dynamic quarter in the title (#2/#3, 2026-07-09): CRO-02/03/10 carry a
  // fiscal_quarter on their resolved target_period (current qtr for CRO-02/10,
  // next qtr for CRO-03). Append it so the title auto-rolls each quarter instead
  // of hardcoding "(Q2)"/"(Q3)" in definitions.json.
  const quarterSuffix =
    data.target_period?.fiscal_quarter != null
      ? ` (Q${String(data.target_period.fiscal_quarter)})`
      : '';
  const displayTitle = `${definition.name}${quarterSuffix}`;

  // UI fallback for tiles whose API target row is missing or mis-routed. When
  // the API returns a real target_value, the API wins (the fallback only
  // activates when target_value === null). Once Plan 11 Task 1's
  // derivePeriodForMetric NQ branch ships, CRO-03 will resolve via the API
  // and this fallback becomes a no-op. CRO-05 needs a long-term cro_metric_target
  // row (the Phase 4 seed deliberately excluded it as a no-target metric, but
  // stakeholder feedback 2026-06-11 says it should have an annual target).
  //
  // multiplier=1 because the Paul Report targets seeded in migration 019 already
  // include the 3x coverage multiplier — they ARE the pipeline-coverage targets,
  // not 1x quotas. Per Chris 2026-06-11. Definitions.json target_multiplier
  // matches (null on CRO-02/03/04/08/09). Tile titles still carry "(3x Target)"
  // for context; the target value IS the 3x coverage requirement.
  const TARGET_FALLBACKS: Record<
    string,
    {
      target: number;
      multiplier: number;
      period: { fiscal_year: number | null; fiscal_quarter: number | null };
    }
  > = {
    // CRO-03 Q4 target now seeded in migration 048 and resolved via the API's
    // NEXT_FISCAL_QUARTER branch, so the stale Q3/$2.43M client fallback was
    // removed (2026-07-09). Do not re-add — it goes stale every quarter.
    // CRO-05: John clarified 2026-06-11 — Total Pipeline Funnel is all open opps,
    // no time restriction, product only. $4.9M is correct. DO NOT apply a target.
    // (Earlier fallback at $2.976M reverted per John's spec.)
  };
  const fallback = TARGET_FALLBACKS[metricId];
  const targetValue =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- HTTP JSON field may be undefined; `??` would change undefined from passthrough to fallback
    data.target_value !== null
      ? data.target_value
      : fallback?.target ?? null;
  const targetPeriod =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- HTTP JSON field may be undefined; `??` would change undefined from passthrough to fallback
    data.target_period !== null
      ? data.target_period
      : fallback?.period ?? null;
  const multiplier = data.target_period?.multiplier ?? fallback?.multiplier ?? 1;
  const denominator = targetValue !== null ? targetValue * multiplier : null;
  const percentToTarget =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- HTTP JSON field may be undefined; `??` would change undefined from passthrough to fallback
    data.percent_to_target !== null
      ? data.percent_to_target
      : denominator !== null && denominator !== 0
        ? data.value / denominator
        : null;

  const hasTarget = targetValue !== null && percentToTarget !== null;
  const periodLabel = formatFiscalPeriod(targetPeriod);
  // Gap to target — distance to the % chip's denominator. Shown alongside the
  // chip so Charles can read at-a-glance "how much more do we need" without
  // doing the % math. Positive gap = behind; non-positive = at/over target.
  const gap = denominator !== null ? denominator - data.value : null;

  return (
    <Card data-testid={`cro-tile-${metricId}`}>
      <CardHeader>
        <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
          {displayTitle}
        </CardTitle>
        <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
      </CardHeader>
      <CardContent>
        {/* KPI value */}
        <p className="text-3xl font-bold tabular-nums text-ink-900" data-testid="kpi-value">
          {formattedValue}
        </p>

        {/* WoW / MoM delta pills (#1). Honest "building" until snapshot history
            reaches back 7d / 30d — never a fabricated delta. */}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <DeltaPill label="WoW" current={data.value} prior={data.prior_week_value} />
          <DeltaPill label="MoM" current={data.value} prior={data.prior_month_value} />
        </div>

        {/* Target sub-text row (omit when no target: CRO-05, CRO-11).
            Shows the DENOMINATOR (target × multiplier) so the value / target
            / % / gap all read consistently. For 3× coverage tiles the
            denominator is what the % chip + gap are computed against; the
            1× quota lives in the parenthetical annotation. */}
        {hasTarget && denominator !== null && (
          <p className="mt-1 text-xs text-ink-500" data-testid="target-label">
            Target: {formatCurrency(denominator)}
            {(() => {
              const parts: string[] = [];
              if (multiplier > 1) parts.push(`${String(multiplier)}× ${formatCurrency(targetValue)}`);
              if (periodLabel) parts.push(periodLabel);
              return parts.length > 0 ? ` (${parts.join(', ')})` : '';
            })()}
          </p>
        )}

        {/* Horizontal completion bar — main-dashboard MainTrendTile style
            (#3, 2026-07-10). Track + fill to % of target (cyan; red when over),
            a 100% target end-tick, and a "% to target / gap / drilldown" row.
            Replaces the old % pill on the big-number target tiles. */}
        {hasTarget && (() => {
          const over = percentToTarget > 1;
          const fillPct = Math.min(100, Math.max(0, Math.round(percentToTarget * 100)));
          return (
            <div className="mt-3" data-testid="progress-to-target">
              <div className="relative mb-1.5 h-[7px] overflow-visible rounded-full bg-ink-100">
                <div
                  className={`h-[7px] rounded-full ${over ? 'bg-bad-text' : 'bg-adeptia-cyan'}`}
                  style={{ width: `${String(fillPct)}%` }}
                  data-testid="percent-chip"
                  aria-label={`${formatPercentToTarget(percentToTarget)} of target`}
                />
                {/* 100% target end-tick */}
                <div
                  aria-hidden
                  className="absolute right-0 top-[-3px] h-[13px] w-0.5 rounded-sm bg-ink-700"
                />
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <span className={`font-semibold tabular-nums ${over ? 'text-good-text' : 'text-ink-700'}`}>
                  {formatPercentToTarget(percentToTarget)} to target
                </span>
                <div className="flex items-center gap-2.5 text-ink-500">
                  {gap !== null && (
                    <span data-testid="gap-to-target" className="tabular-nums">
                      {gap > 0
                        ? `${formatCurrency(gap)} to go`
                        : `${formatCurrency(Math.abs(gap))} over`}
                    </span>
                  )}
                  <Tooltip
                    trigger={<span className="text-brand-600 hover:text-brand-700 hover:underline cursor-pointer font-medium">source →</span>}
                    label="Salesforce drilldown"
                  >
                    <p className="text-ink-700">Drilldown to Salesforce records — Phase 6</p>
                  </Tooltip>
                </div>
              </div>
            </div>
          );
        })()}

        {/* When no target: still show the drilldown placeholder */}
        {!hasTarget && (
          <div className="mt-3 flex justify-end">
            <Tooltip
              trigger={<span className="text-brand-600 hover:text-brand-700 hover:underline cursor-pointer text-xs font-medium">View source →</span>}
              label="Salesforce drilldown"
            >
              <p className="text-ink-700">Drilldown to Salesforce records — Phase 6</p>
            </Tooltip>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
